// library/view-model — the list exactly as shown, projected from the module's
// Model, the frame's query text and opened row, and the host's pool. Pure:
// LibraryView renders it; prev/next, the keyboard and the marked row all walk
// the same `order` (the table's sort), so what the author sees is what they
// step through.

import type { SmartFilter } from "../../app/ports/workspace.js";
import {
    type ContentSection,
    contentSection,
    navOrder,
} from "../../components/Search/contentSearch.js";
import {
    type ListPosition,
    positionIn,
} from "../../components/Search/DrillInBar.js";
import {
    hintOf,
    type QueryHint,
    type QueryScope,
    scopeOfQuery,
    type Token,
    tokenize,
    withScope,
} from "../../components/Search/libraryQuery.js";
import { scopeCount, scopesFor } from "../../components/Search/libraryScope.js";
import {
    compareBy,
    type LibraryResults,
    type LibrarySort,
    rankLibrary,
    rowsOf,
    type SearchResult,
    type SearchResultKind,
    separateSources,
} from "../../components/Search/searchModel.js";
import type { LibraryModel } from "./model.js";

export type LibraryProjection = {
    readonly tokens: readonly Token[];
    // The tab the text selects (null: several kinds, no single tab).
    readonly scope: QueryScope | null;
    readonly scopes: readonly QueryScope[];
    // Each tab's row count (its pill shows it).
    readonly scopeCounts: ReadonlyMap<QueryScope, number>;
    readonly ranked: LibraryResults;
    readonly content: ContentSection;
    // Title rows, then content rows: the list as shown.
    readonly order: readonly SearchResult[];
    readonly markedIndex: number;
    // The row gone into, and where it sits in `order`.
    readonly opened: SearchResult | null;
    readonly position: ListPosition | undefined;
    // A filter in the text that does nothing (the field says so).
    readonly hint: QueryHint | undefined;
    // How many ARCHIVED rows the same query finds, counted only when the
    // live list finds nothing (the empty state offers them in one click).
    readonly archivedMatches: number;
};

// Title rows + content rows the query finds in `pool`, in list order.
const found = (
    model: LibraryModel,
    pool: readonly SearchResult[],
    query: string,
): {
    readonly ranked: LibraryResults;
    readonly content: ContentSection;
    readonly order: readonly SearchResult[];
} => {
    const ranking = rankLibrary(query, model.sort, pool);
    const results = separateSources(ranking.results);
    const titleRows = rowsOf(results);
    const found = contentSection(
        model.content,
        ranking.contentWords,
        ranking.pool,
        new Set(titleRows.map((row) => row.result.id)),
    );
    // Content hits sort like the table (the server answers in its own order).
    const byTable = compareBy(model.sort);
    const content: ContentSection =
        found.type === "rows"
            ? {
                  ...found,
                  rows: [...found.rows].sort((a, b) =>
                      byTable(a.result, b.result),
                  ),
              }
            : found;
    return {
        ranked: results,
        content,
        order: navOrder(
            titleRows.map((row) => row.result),
            content,
        ),
    };
};

export const projectLibrary = (
    model: LibraryModel,
    pool: readonly SearchResult[],
    visibleKinds: readonly SearchResultKind[],
    query: string,
    openedId: string | null,
): LibraryProjection => {
    const tokens = tokenize(query);
    const scope = scopeOfQuery(tokens);
    const { ranked, content, order } = found(model, pool, query);
    // Nothing live: does the archive hold it? (The content read is the live
    // query's; its words are the same.)
    const archivedMatches =
        scope === "archived" ||
        order.length > 0 ||
        content.type === "loading" ||
        query.trim() === ""
            ? 0
            : found(model, pool, withScope(query, "archived")).order.length;
    const opened =
        openedId === null
            ? null
            : (pool.find((r) => r.id === openedId) ?? null);
    const scopes = scopesFor(visibleKinds, pool, scope);
    return {
        tokens,
        scope,
        scopes,
        scopeCounts: new Map(scopes.map((s) => [s, scopeCount(s, pool)])),
        ranked,
        content,
        order,
        markedIndex: order.findIndex((r) => r.id === model.marked),
        opened,
        position: opened === null ? undefined : positionIn(order, opened.id),
        hint: hintOf(query),
        archivedMatches,
    };
};

// === Saved searches (Smart Filters) ===

type SmartFilterChip = {
    readonly filter: SmartFilter;
    // The search shown IS this one (its query text and its sort).
    readonly current: boolean;
};

export type SmartFiltersProjection = {
    readonly chips: readonly SmartFilterChip[];
    // The one whose menu shows: the last applied, else the current one.
    readonly focused: SmartFilter | null;
    // "Save search": a non-empty search no saved one shows.
    readonly canSave: boolean;
    // The focused one can be updated to the search shown.
    readonly canUpdate: boolean;
    // One was just deleted: "Search removed · Undo" until the next action.
    readonly canUndoDelete: boolean;
};

const sameSort = (a: LibrarySort, b: LibrarySort): boolean =>
    a.column === b.column && a.direction === b.direction;

export const projectSmartFilters = (
    model: LibraryModel,
    filters: readonly SmartFilter[],
    query: string,
): SmartFiltersProjection => {
    const text = query.trim();
    const isCurrent = (f: SmartFilter): boolean =>
        f.query.trim() === text && sameSort(f.sort, model.sort);
    const chips = filters.map((filter) => ({
        filter,
        current: isCurrent(filter),
    }));
    const focused =
        filters.find((f) => f.id === model.applied) ??
        filters.find(isCurrent) ??
        null;
    return {
        chips,
        focused,
        canSave: text !== "" && !chips.some((c) => c.current),
        canUpdate: focused !== null && text !== "" && !isCurrent(focused),
        canUndoDelete: model.deletedSmartFilter !== null,
    };
};
